import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import {
  CANVAS_NODE_CATALOG,
  CANVAS_NODE_CATEGORY_ICON,
  CANVAS_NODE_CATEGORY_LABEL_KEY,
} from '@/features/studio/canvas/canvas-node-catalog'
import type { StudioNodeType } from '@/features/studio/canvas/types'

export type CanvasPaneContextMenuState = {
  x: number
  y: number
  flowX: number
  flowY: number
}

export function CanvasPaneContextMenu({
  open,
  anchor,
  onClose,
  onAdd,
  hideNodeTypes,
}: {
  open: boolean
  anchor: CanvasPaneContextMenuState | null
  onClose: () => void
  onAdd: (nodeType: StudioNodeType, flowPosition: { x: number; y: number }) => void
  hideNodeTypes?: StudioNodeType[]
}) {
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    const onPointerDown = (e: PointerEvent) => {
      const menu = menuRef.current
      if (menu && !menu.contains(e.target as Node)) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [open, onClose])

  if (!open || !anchor) return null

  const hidden = new Set(hideNodeTypes ?? [])
  const flowPosition = { x: anchor.flowX, y: anchor.flowY }

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      data-canvas-pane-menu
      className={clsx(
        'studio-canvas-context-menu studio-scrollbar-hidden',
        'fixed z-[10050] w-60 max-h-[min(72vh,520px)] overflow-y-auto overscroll-contain',
        'rounded-lg border border-white/10 bg-[#141418] p-0 text-popover-foreground shadow-xl ring-1 ring-foreground/10',
      )}
      style={{ left: anchor.x, top: anchor.y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="border-b px-3 pb-2 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {t('canvas.dockTitle')}
      </div>
      {CANVAS_NODE_CATALOG.map((group, groupIndex) => {
        const items = group.items.filter((item) => !hidden.has(item.type))
        if (items.length === 0) return null
        const CatIcon = CANVAS_NODE_CATEGORY_ICON[group.category]
        return (
          <div key={group.category} className="px-1.5 py-1">
            {groupIndex > 0 ? <div className="my-1 h-px bg-white/[0.06]" /> : null}
            <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <CatIcon className="h-3 w-3 text-indigo-400/80" aria-hidden />
              {t(CANVAS_NODE_CATEGORY_LABEL_KEY[group.category])}
            </div>
            {items.map(({ type, icon: Icon, labelKey }) => (
              <button
                key={type}
                type="button"
                role="menuitem"
                className={clsx(
                  'flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-zinc-200',
                  'hover:bg-indigo-500/10 hover:text-indigo-100 focus-visible:bg-indigo-500/10 focus-visible:outline-none',
                )}
                onClick={() => {
                  onAdd(type, flowPosition)
                  onClose()
                }}
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-white/[0.08] bg-[#0e0e16] text-zinc-400">
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                </span>
                <span className="min-w-0 truncate">{t(labelKey)}</span>
              </button>
            ))}
          </div>
        )
      })}
    </div>,
    document.body,
  )
}
